<template>
  <div id="file-drag-drop">
    <div class="tag-selector mb-3">
      <span
        v-for="(tag, index) in tags"
        :key="tag"
        @click="removeTag(index)"
        class="tag"
        >{{ tag }}</span
      >
      <input
        type="text"
        v-model.trim="tag"
        @keydown.delete="tag == '' ? removeTag(tags.length - 1) : ''"
        @keypress.enter="addTag"
        @keypress.space="addTag"
        placeholder="Tag: *"
      />
    </div>

    <template>
      <input
        v-if="isMobile || !dragAndDropCapable"
        type="file"
        @change="addFiles"
        multiple
      />
      <form
        v-else
        @dragover.stop.prevent
        @drop.stop.prevent="addFiles"
        ref="fileform"
        :style="{
          height: files.length == 0 ? '50vh' : '30vh',
          'background-position-y': 100 - uploadPercentage + '%'
        }"
      >
        <span class="drop-files">{{ display }}</span>
      </form>
    </template>

    <button
      :disabled="disableForm"
      @click="uploadDocument"
      v-if="files.length > 0 && tags.length > 0"
      class="mt-4 submit btn btn-primary btn-block py-3"
    >
      Upload Images
    </button>
    <div class="file-listing">
      <div class="file" v-for="(file, key) in files" :key="file.name">
        <div @click="files.splice(key, 1)" class="delete-overlay">X</div>
        <img class="preview" :ref="'preview' + parseInt(key)" />
        <span class="d-block">{{
          file.name.slice(0, 8) + "..." + file.name.slice(-3)
        }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { storage, imagesCollection } from "../firebase-config";
import { mapGetters } from "vuex";
import compressImage from "browser-image-compression";

export default {
  data() {
    return {
      files: [],
      tags: [],
      allowedTags: ["designs", "logos", "illustrations", "user-interface"],
      tag: "",
      display: "Drop the files here!",
      uploadPercentage: 0,
      disableForm: false
    };
  },
  methods: {
    addFiles(e) {
      let files;
      if (e.type === "change") {
        files = e.target.files;
      } else {
        files = e.dataTransfer.files;
      }
      for (let i = 0; i < files.length; i++) {
        this.files.push(files[i]);
      }
      this.getImagePreviews();
    },
    getImagePreviews() {
      for (let i = 0; i < this.files.length; i++) {
        //   check if file is an image
        if (/\.(jpe?g|png|gif)$/i.test(this.files[i].name)) {
          let reader = new FileReader();

          reader.addEventListener("load", () => {
            this.$refs["preview" + parseInt(i)][0].src = reader.result;
          });

          // Read the data for the file in through the reader. When it has been loaded, we listen to the event propagated and set the image src to what was loaded from the reader.

          reader.readAsDataURL(this.files[i]);
        } else {
          // We do the next tick so the reference is bound and we can access it.

          this.$nextTick(() => {
            this.$refs["preview" + parseInt(i)][0].src = "/images/file.png";
          });
        }
      }
    },
    addTag() {
      if (this.allowedTags.includes(this.tag) && this.tags.length < 1) {
        this.tags.push(this.tag);
        this.tag = "";
      } else {
        console.error(
          "Not a valid tag. Please choose either:" + this.tags.map(tag => tag)
        );
      }
    },
    removeTag(index) {
      this.tags.splice(index, 1);
    },
    uploadFiles(file, fileName) {
      let storageRef = storage.ref();
      var uploadTask = storageRef.child(`images/${fileName}`).put(file);

      const promise = new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          snapshot => {
            // Observe state change events such as progress, pause, and resume
            // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
            this.uploadPercentage =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            this.display =
              `Uploading ${file.name} to Storage  \n.
              We are ` +
              Math.ceil(this.uploadPercentage) +
              "% done";
          },
          error => {
            this.display = "Something went wrong while uploading " + file.name;
            console.log(error);
          },
          () => {
            storageRef
              .child(`images/${fileName}`)
              .getDownloadURL()
              .then(downloadURL => {
                resolve(downloadURL);
              })
              .catch(err => reject(err));
          }
        );
      });

      return promise;
    },
    uploadTags(downloadURL, fileName, originalName) {
      this.display = `Writing ${fileName} to Database`;
      imagesCollection
        .add({
          createdOn: new Date(),
          url: downloadURL,
          tags: this.tags[0],
          userId: this.currentUser.uid,
          creator: this.userProfile.name,
          name: fileName,
          project: fileName
        })
        .then(() => {
          this.display = originalName + " saved to Database";
        })
        .catch(err => {
          console.log(err);
        });
    },
    async uploadDocument() {
      if (this.tags.length < 1 || this.files.length < 1) {
        alert("Please select files and a tag to upload");
      }
      this.disableForm = true;

      for (let file of this.files) {
        let baseName = this.generateRandomName();
        let fileName = `${baseName}.${file.name.split(".")[1]}`;
        const options = {
          maxSizeMB: 0.2,
          onProgress: () => {
            this.display = "Compressing " + file.name;
          }
        };
        try {
          const compressedFile =
            file.type === "image/gif"
              ? file
              : await compressImage(file, options);
          const downloadURL = await this.uploadFiles(compressedFile, fileName);
          await this.uploadTags(downloadURL, fileName, file.name);
        } catch (err) {
          this.display = "Something went wrong";
          console.error(err);
        }
      }

      this.uploadPercentage = 0;
      this.display = "Drop the files here!";
      this.tags = [];
      this.files = [];
      this.disableForm = false;
    },
    generateRandomName() {
      var randomNumber = Math.random() * Date.now() * 1000;
      return randomNumber.toString(16).split(".")[0];
    }
  },
  computed: {
    dragAndDropCapable() {
      // Create a test element to see if certain events are present that let us do drag and drop.
      var div = document.createElement("div");
      // Check to see if the `draggable` event is in the element or the `ondragstart` and `ondrop` events are in the element.
      return (
        ("draggable" in div || ("ondragstart" in div && "ondrop" in div)) &&
        "FormData" in window &&
        "FileReader" in window
      );
    },
    isMobile() {
      return window.innerWidth < 750;
    },
    ...mapGetters(["userProfile", "currentUser"])
  }
};
</script>

<style lang="scss" scoped>
#file-drag-drop {
  .tag-selector {
    background-color: #14151a;
    display: flex;
    align-items: center;

    .tag {
      margin: 10px;
      margin-right: 0;
      display: inline-block;
      height: 25%;
      background: white;
      font-weight: 900;
      padding: 10px 20px;
      color: black;
      border-radius: 100px;
      white-space: nowrap;
      transition: 0.2s background ease;

      &:hover {
        background: $primary;
      }
    }
  }

  input {
    position: relative;
    width: 100%;
    padding: 20px 20px;
    font-weight: 400;
    letter-spacing: 1px;
    font-size: 15px;
    line-height: 1.7;
    background-color: #14151a;
    color: #fff;
    border: none;
    text-align: left;
    cursor: text;
    transition: all 250ms linear;

    &:active,
    &:hover {
      color: #ccc;
    }

    &:focus {
      outline: none !important;
    }

    &::placeholder {
      font-size: 15px;
      letter-spacing: 1px;
      color: rgba(255, 255, 255, 0.4);
      font-weight: 700;
    }
  }
}

form {
  background: linear-gradient($primary 50%, black 50%);
  background-size: 100% 200%;
  transition: 0.5s all ease;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;

  span {
    font-weight: 16px;
  }
}

.file-listing {
  display: grid;
  grid-template-columns: repeat(auto-fill, 100px);
  grid-gap: 10px;
  margin-top: 20px;

  .file {
    position: relative;

    img {
      height: 100px;
      width: 100%;
      object-fit: cover;
      border-radius: 10px;
    }

    .delete-overlay {
      position: absolute;
      border-radius: 10px;
      display: grid;
      place-items: center;
      width: 100px;
      height: 100px;
      color: black;
      font-weight: 900;
      font-size: 15px;
      background: rgba($color: $primary, $alpha: 0.5);
      opacity: 0;
      transition: 0.2s opacity ease;

      &:hover {
        opacity: 1;
      }
    }
  }
}
</style>
